import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Col, FormGroup, Input, Label, Row, Spinner, Table } from "reactstrap";

import { useAuth } from "../../../hooks/auth";
import { useCompanies } from "../../../hooks/companies";
import { useActiveProduct } from "../../../hooks/activeProduct";
import PageHero, { PageHeroRefreshButton } from "../../../components/PageHero";
import FilterBand from "../../../components/FilterBand";
import ShellCard, { TableSurface } from "../../../components/ShellCard";
import SpecificEmpresaRequiredNotice from "../../../components/SpecificEmpresaRequiredNotice";
import { listarEnviosComErro, listarLotes, listarTransportadorasPedidos, reenviarEnvios } from "../../../services/despachoApi";

import { temPermissao } from "../AuditoriaPedido/regras";
import {
  Aviso,
  CheckboxTodos,
  EstadoTabela,
  Pill,
  SelecaoResumo,
  eyebrowDespacho,
  extractError,
  fieldInputStyle,
  fmtDataHora,
  fmtNumber,
  useLista,
  useSelecao,
} from "../_documentosFrete/comum";
import "../_documentosFrete/styles.css";
import "./styles.css";
import GatilhosModal from "./GatilhosModal";
import LoteDetalheModal, { TEXTO_COBRANCA_REENVIO } from "./LoteDetalheModal";
import { ABAS, ABA_COM_ERRO, PERMISSAO_EDITAR, linkDaUrl, nomeTransportadora, rotuloOrigem, rotuloStatusLote, textoReenvio, tomStatusLote } from "./regras";

// Despacho > Lotes (PO1-2377, épico PO1-2374): o lote de despacho (o "PLP" do
// mercado) agrupa, por transportadora, os envios com etiqueta emitida
// (NotaFiscalEnvio, PO1-3378). Abas Abertos / Fechados / Despachados listam os
// lotes do embarcador; "Com erro" lista os envios com falha (em lote ou não)
// para reenviar. O detalhe do lote (modal) desvincula, reenvia, fecha e
// despacha, e baixa o manifesto e o NOTFIS. Os gatilhos de fechamento
// (horário / volume) ficam no modal próprio. Leitura com
// hub.operacao.despacho.visualizar (menu); escrita com hub.operacao.despacho.editar.
//
// Link direto (PO1-3493): `?lote=<id>` abre o detalhe do lote ao montar e
// `?aba=<chave>` escolhe a aba (atalhos da tela de Indicadores de despacho).
// `location` vem do <Route>; sem ele (testes) a tela abre normal.

const filtrosIniciais = () => ({ idTransportadora: "", de: "", ate: "" });

export default function DespachoLotes({ location }) {
  const { user } = useAuth();
  const { activeProduct } = useActiveProduct();
  const podeEditar = temPermissao(user, PERMISSAO_EDITAR);
  const { empresaId, hasEmpresaSelection } = useCompanies();

  // Só o primeiro render lê a URL: depois a tela manda no próprio estado.
  const [link] = useState(() => linkDaUrl(location && location.search));

  const transportadoras = useLista(listarTransportadorasPedidos, hasEmpresaSelection ? `emb-${empresaId}` : null);
  const [aba, setAba] = useState(link.aba || ABAS[0].chave);
  const [draft, setDraft] = useState(filtrosIniciais);
  const [filtros, setFiltros] = useState(filtrosIniciais);

  const [lotes, setLotes] = useState([]);
  const [comErro, setComErro] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [erroFiltro, setErroFiltro] = useState("");
  const [aviso, setAviso] = useState(null); // { tom, texto }
  const [reenviando, setReenviando] = useState(false);
  const [confirmarReenvio, setConfirmarReenvio] = useState(false);

  // { id, codigo }; pelo link só vem o id (o modal busca o resto por ele).
  const [detalhe, setDetalhe] = useState(link.lote ? { id: link.lote } : null);
  const [gatilhosAberto, setGatilhosAberto] = useState(false);
  const selecaoErros = useSelecao();

  const abaComErro = aba === ABA_COM_ERRO;

  // Trocar de aba rápido dispara duas consultas; só a ÚLTIMA pode escrever no
  // estado, senão a resposta atrasada da aba anterior sobrescreve a atual.
  const versaoConsulta = useRef(0);
  const carregar = useCallback(async () => {
    versaoConsulta.current += 1;
    const versao = versaoConsulta.current;
    const atual = () => versao === versaoConsulta.current;
    setLoading(true);
    setErro("");
    try {
      if (aba === ABA_COM_ERRO) {
        const envios = await listarEnviosComErro();
        if (!atual()) return;
        setComErro(envios.map((e) => ({ ...e, id: e.idEnvio })));
      } else {
        const params = { status: aba };
        if (filtros.idTransportadora) params.idTransportadora = Number(filtros.idTransportadora);
        if (filtros.de) params.de = filtros.de;
        if (filtros.ate) params.ate = filtros.ate;
        const resposta = await listarLotes(params);
        if (!atual()) return;
        setLotes(resposta);
      }
    } catch (e) {
      if (!atual()) return;
      setLotes([]);
      setComErro([]);
      setErro(extractError(e, "Não foi possível consultar os lotes de despacho."));
    } finally {
      if (atual()) setLoading(false);
    }
  }, [aba, filtros]);

  useEffect(() => {
    if (!hasEmpresaSelection) return;
    carregar();
  }, [carregar, hasEmpresaSelection]);

  // Troca de embarcador no cabeçalho: filtros e seleção eram do anterior.
  // Só conta como troca quando já HAVIA um embarcador: o primeiro a chegar
  // (seleção carregando) não pode fechar o modal aberto pelo link.
  const empresaAnterior = useRef(empresaId);
  useEffect(() => {
    const trocou = Boolean(empresaAnterior.current) && empresaAnterior.current !== empresaId;
    empresaAnterior.current = empresaId;
    if (!trocou) return;
    const base = filtrosIniciais();
    setErroFiltro("");
    setAviso(null);
    setDetalhe(null);
    setGatilhosAberto(false);
    setDraft(base);
    setFiltros(base);
    selecaoErros.limpar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaId]);

  // Lê o valor ANTES do setState com updater (event pooling do React 16).
  const updateDraft = (campo) => (e) => {
    const valor = e.target.value;
    setDraft((atual) => ({ ...atual, [campo]: valor }));
  };

  const aplicar = () => {
    if (draft.de && draft.ate && draft.de > draft.ate) {
      setErroFiltro("A data inicial é maior que a final.");
      return;
    }
    setErroFiltro("");
    setFiltros({ ...draft });
  };
  const limpar = () => {
    const base = filtrosIniciais();
    setErroFiltro("");
    setDraft(base);
    setFiltros(base);
  };

  const trocarAba = (chave) => {
    if (chave === aba) return;
    setAviso(null);
    setConfirmarReenvio(false);
    selecaoErros.limpar();
    setAba(chave);
  };

  const recarregar = () => {
    setAviso(null);
    carregar();
  };

  // Seleção mudou: a confirmação era dos envios marcados antes.
  const qtdErrosSelecionados = selecaoErros.ids.length;
  useEffect(() => {
    setConfirmarReenvio(false);
  }, [qtdErrosSelecionados]);

  // Reenviar gera etiqueta e cobrança novas: primeiro o passo de confirmação,
  // depois o POST com confirmarCobranca (despachoApi). Travado durante a chamada.
  const reenviarSelecionados = async () => {
    if (reenviando || !selecaoErros.ids.length) return;
    setReenviando(true);
    setAviso(null);
    try {
      const r = await reenviarEnvios(selecaoErros.ids);
      const falhou = (r.resultados || []).some((x) => !x.ok);
      setAviso({ tom: falhou ? "aviso" : "ok", texto: textoReenvio(r) });
      selecaoErros.limpar();
      setConfirmarReenvio(false);
      await carregar();
    } catch (e) {
      setAviso({ tom: "erro", texto: extractError(e, "Não foi possível reenviar os envios.") });
    } finally {
      setReenviando(false);
    }
  };

  if (!hasEmpresaSelection) {
    return (
      <div className="content docs-frete">
        <SpecificEmpresaRequiredNotice message="Selecione um embarcador específico para ver os lotes de despacho." />
      </div>
    );
  }

  const activeFiltersCount = [filtros.idTransportadora, filtros.de, filtros.ate].filter(Boolean).length;
  const rotuloAba = (ABAS.find((a) => a.chave === aba) || ABAS[0]).rotulo;

  return (
    <div className="content docs-frete">
      <PageHero
        eyebrow={eyebrowDespacho(activeProduct)}
        title="Lotes de despacho"
        description="Os envios com etiqueta emitida entram no lote aberto da transportadora. Feche o lote (à mão ou por gatilho), baixe o manifesto e o NOTFIS e despache, com ou sem pedido de coleta."
        actions={
          <>
            <PageHeroRefreshButton onClick={recarregar} loading={loading} />
            <Button color="default" size="sm" outline onClick={() => setGatilhosAberto(true)} title="Fechamento automático por horário ou por volume">
              <i className="tim-icons icon-time-alarm" style={{ marginRight: 6 }} />
              Gatilhos
            </Button>
          </>
        }
      />

      {!abaComErro ? (
        <FilterBand
          mode="server"
          searchLabel="Filtros"
          searchHint={activeFiltersCount ? `${activeFiltersCount} filtro(s) ativo(s)` : "Todos os lotes"}
          searchPlaceholder="Transportadora e período de criação no painel de filtros"
          searchDisabled
          showFiltersButton
          activeFiltersCount={activeFiltersCount}
          panelTitle="Transportadora e período"
          panelDescription="O período é o de criação do lote; deixe em branco para ver todos."
          onApply={aplicar}
          onClear={limpar}
          applyLabel="Buscar"
          busy={loading}
        >
          <Row>
            <Col md="4">
              <FormGroup>
                <Label for="lotes-filtro-transportadora">Transportadora</Label>
                <Input id="lotes-filtro-transportadora" type="select" value={draft.idTransportadora} onChange={updateDraft("idTransportadora")} style={fieldInputStyle}>
                  <option value="">Todas</option>
                  {transportadoras.map((t) => (
                    <option key={t.id} value={t.id}>
                      {nomeTransportadora(t)}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md="2">
              <FormGroup>
                <Label for="lotes-filtro-de">Criado de</Label>
                <Input id="lotes-filtro-de" type="date" value={draft.de} onChange={updateDraft("de")} style={fieldInputStyle} />
              </FormGroup>
            </Col>
            <Col md="2">
              <FormGroup>
                <Label for="lotes-filtro-ate">Até</Label>
                <Input id="lotes-filtro-ate" type="date" value={draft.ate} onChange={updateDraft("ate")} style={fieldInputStyle} />
              </FormGroup>
            </Col>
          </Row>
          {erroFiltro ? <Aviso tom="erro">{erroFiltro}</Aviso> : null}
        </FilterBand>
      ) : null}

      <ShellCard
        title={abaComErro ? "Envios com erro" : `Lotes ${rotuloAba.toLowerCase()}`}
        description={abaComErro ? `${fmtNumber(comErro.length)} envio(s) com falha na emissão, em lote ou não.` : `${fmtNumber(lotes.length)} lote(s).`}
        action={
          <div className="docs-frete__abas" role="tablist" aria-label="Situação do lote">
            {ABAS.map((a) => (
              <button key={a.chave} type="button" role="tab" aria-selected={aba === a.chave} className={`docs-frete__aba${aba === a.chave ? " is-active" : ""}`} onClick={() => trocarAba(a.chave)}>
                {a.rotulo}
              </button>
            ))}
          </div>
        }
      >
        {aviso ? <Aviso tom={aviso.tom}>{aviso.texto}</Aviso> : null}
        <TableSurface>
          {abaComErro && podeEditar ? (
            <SelecaoResumo totais={selecaoErros.totais} rotulo="envio(s)" semValor>
              <Button color="primary" size="sm" className="btn-simple" onClick={() => setConfirmarReenvio(true)} disabled={!selecaoErros.ids.length || reenviando || confirmarReenvio}>
                Reenviar selecionados
              </Button>
            </SelecaoResumo>
          ) : null}
          {abaComErro && podeEditar && confirmarReenvio ? (
            <Aviso tom="aviso" role="alertdialog">
              Reenviar <strong>{fmtNumber(selecaoErros.ids.length)}</strong> envio(s) com falha? {TEXTO_COBRANCA_REENVIO}
              <div className="docs-frete__acoes">
                <Button color="primary" size="sm" onClick={reenviarSelecionados} disabled={reenviando}>
                  {reenviando ? <Spinner size="sm" style={{ marginRight: 6 }} /> : null}
                  Confirmar reenvio
                </Button>
                <Button color="default" size="sm" outline onClick={() => setConfirmarReenvio(false)} disabled={reenviando}>
                  Voltar
                </Button>
              </div>
            </Aviso>
          ) : null}
          <EstadoTabela
            loading={loading}
            erro={erro}
            vazio={!loading && (abaComErro ? !comErro.length : !lotes.length)}
            textoCarregando={abaComErro ? "Consultando envios com erro..." : "Consultando lotes..."}
            textoVazio={abaComErro ? "Nenhum envio com erro." : `Nenhum lote ${rotuloAba.toLowerCase().replace(/s$/, "")} para os filtros.`}
          />
          {!loading && !erro && abaComErro && comErro.length ? (
            <Table className="docs-frete__tabela hub-thead-accent" size="sm" responsive hover>
              <thead className="text-primary">
                <tr>
                  {podeEditar ? (
                    <th className="is-sel">
                      <CheckboxTodos itens={comErro} selecao={selecaoErros} label="Selecionar todos os envios com erro" />
                    </th>
                  ) : null}
                  <th>Nota fiscal</th>
                  <th>Pedido</th>
                  <th>Transportadora</th>
                  <th>Serviço</th>
                  <th>Erro</th>
                  <th>Lote</th>
                  <th>Quando</th>
                </tr>
              </thead>
              <tbody>
                {comErro.map((e) => (
                  <tr key={e.idEnvio}>
                    {podeEditar ? (
                      <td className="is-sel">
                        <input type="checkbox" aria-label={`Selecionar envio da NF ${e.numero}`} checked={selecaoErros.has(e.idEnvio)} onChange={() => selecaoErros.toggle(e)} />
                      </td>
                    ) : null}
                    <td>
                      <strong>{e.numero}</strong>
                    </td>
                    <td>{e.pedido || "-"}</td>
                    <td>{e.transportadora || "-"}</td>
                    <td>{e.servico || "-"}</td>
                    <td>
                      <small className="despacho-lotes__erro">{e.erro || "-"}</small>
                    </td>
                    <td>
                      {e.idLote ? (
                        <Button color="primary" size="sm" className="btn-simple" onClick={() => setDetalhe({ id: e.idLote, codigo: e.codigoLote })}>
                          {e.codigoLote || `#${e.idLote}`}
                        </Button>
                      ) : (
                        <Pill tom="info" title="Envio ainda sem lote">
                          Sem lote
                        </Pill>
                      )}
                    </td>
                    <td className="docs-frete__nowrap">{fmtDataHora(e.creationTime)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : null}
          {!loading && !erro && !abaComErro && lotes.length ? (
            <Table className="docs-frete__tabela hub-thead-accent" size="sm" responsive hover>
              <thead className="text-primary">
                <tr>
                  <th>Lote</th>
                  <th>Transportadora</th>
                  <th>Situação</th>
                  <th>Origem</th>
                  <th className="is-number">Envios</th>
                  <th className="is-number">Pedidos</th>
                  <th>Criado em</th>
                  <th>Fechado em</th>
                  <th>Despachado em</th>
                  <th>Coleta</th>
                  <th aria-label="Ações" />
                </tr>
              </thead>
              <tbody>
                {lotes.map((l) => (
                  <tr key={l.id}>
                    <td>
                      <strong>{l.codigo || `#${l.id}`}</strong>
                    </td>
                    <td>{l.transportadora || "-"}</td>
                    <td>
                      <Pill tom={tomStatusLote(l.status)}>{rotuloStatusLote(l.status)}</Pill>
                    </td>
                    <td>{rotuloOrigem(l.origem)}</td>
                    <td className="is-number">{fmtNumber(l.qtdEnvios)}</td>
                    <td className="is-number">{fmtNumber(l.qtdPedidos)}</td>
                    <td className="docs-frete__nowrap">{fmtDataHora(l.creationTime)}</td>
                    <td className="docs-frete__nowrap">{fmtDataHora(l.datFechamento)}</td>
                    <td className="docs-frete__nowrap">{fmtDataHora(l.datDespacho)}</td>
                    <td>{l.numeroColeta || "-"}</td>
                    <td>
                      <div className="docs-frete__acoes">
                        <Button color="primary" size="sm" className="btn-simple" onClick={() => setDetalhe({ id: l.id, codigo: l.codigo })}>
                          Abrir
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : null}
        </TableSurface>
      </ShellCard>

      {detalhe ? (
        <LoteDetalheModal
          idLote={detalhe.id}
          podeEditar={podeEditar}
          onClose={(alterou) => {
            setDetalhe(null);
            if (alterou) carregar();
          }}
        />
      ) : null}

      {gatilhosAberto ? <GatilhosModal transportadoras={transportadoras} podeEditar={podeEditar} onClose={() => setGatilhosAberto(false)} /> : null}
    </div>
  );
}
